import { useMutation } from "@tanstack/react-query";
import { Check, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";

import { Button, Dialog, TextField } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney, plural } from "@/lib/format";
import { Decimal, money } from "@/lib/money";
import { localShiftRepository } from "@/repositories/localShiftRepository";
import { shiftsRepository, type Shift } from "@/repositories/shiftsRepository";
import { useAuthStore } from "@/stores/authStore";

/**
 * South African notes and coins, largest first.
 *
 * The 1c, 2c and 5c coins were withdrawn and are not listed. Counting by
 * denomination rather than by typing a total is not decoration: it is how a
 * drawer is actually counted, and a shopkeeper who types R1 240 straight into a
 * box has no way to find the mistake when the till is out by R100.
 */
const DENOMINATIONS = [
  { value: "200.00", label: "R200", kind: "note" },
  { value: "100.00", label: "R100", kind: "note" },
  { value: "50.00", label: "R50", kind: "note" },
  { value: "20.00", label: "R20", kind: "note" },
  { value: "10.00", label: "R10", kind: "note" },
  { value: "5.00", label: "R5", kind: "coin" },
  { value: "2.00", label: "R2", kind: "coin" },
  { value: "1.00", label: "R1", kind: "coin" },
  { value: "0.50", label: "50c", kind: "coin" },
  { value: "0.20", label: "20c", kind: "coin" },
  { value: "0.10", label: "10c", kind: "coin" },
] as const;

/**
 * Counting the drawer and closing the shift.
 *
 * The one rule that shapes this screen: **the expected figure is not shown
 * before the count is submitted** — to anybody, manager included. The server
 * withholds it from a cashier outright, and even where a manager may see it, a
 * person who knows what the drawer should hold is not counting it, they are
 * confirming it. A shortage that could have been quietly covered from a pocket
 * never appears, and the whole exercise measures nothing.
 *
 * So the count goes in blind, and the variance is revealed afterwards.
 */
export function CashUpDialog({
  shift,
  onClose,
  onDone,
}: {
  shift: Shift;
  onClose: () => void;
  onDone: () => void;
}) {
  const can = useAuthStore((s) => s.can);

  const [counts, setCounts] = useState<Record<string, string>>({});
  const [typedTotal, setTypedTotal] = useState("");
  const [byDenomination, setByDenomination] = useState(true);
  const [note, setNote] = useState("");
  const [closed, setClosed] = useState<Shift | null>(null);
  const [queued, setQueued] = useState(false);

  const countedTotal = useMemo(() => {
    if (!byDenomination) {
      const parsed = Number(typedTotal);
      return Number.isFinite(parsed) && parsed >= 0 ? money(typedTotal || "0") : null;
    }
    return DENOMINATIONS.reduce((sum, denomination) => {
      const quantity = Number(counts[denomination.value] ?? "");
      if (!Number.isFinite(quantity) || quantity < 0) return sum;
      return sum.plus(new Decimal(denomination.value).times(Math.floor(quantity)));
    }, new Decimal(0));
  }, [byDenomination, counts, typedTotal]);

  const close = useMutation({
    mutationFn: () =>
      localShiftRepository.close({
        shiftId: shift.id,
        countedCash: (countedTotal ?? new Decimal(0)).toFixed(2),
        note: note.trim(),
      }),
    onSuccess: (outcome) => {
      setClosed(outcome.shift);
      setQueued(outcome.queued);
    },
  });

  const approve = useMutation({
    mutationFn: () => shiftsRepository.approve(shift.id, note.trim()),
    onSuccess: (updated) => setClosed(updated),
  });

  if (closed) {
    return (
      <Outcome
        shift={closed}
        queued={queued}
        canApprove={can("shifts.approve_variance")}
        approving={approve.isPending}
        onApprove={() => approve.mutate()}
        onClose={() => {
          onDone();
          onClose();
        }}
      />
    );
  }

  const totals = shift.totals;

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title="Cash up"
      description="Count what is physically in the drawer. The till works out the difference afterwards."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={close.isPending}
            disabled={countedTotal === null}
            onClick={() => close.mutate()}
          >
            Close shift
          </Button>
        </>
      }
    >
      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_16rem]">
        {/* ── The count ───────────────────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-ink-900">What is in the drawer</p>
            <button
              type="button"
              onClick={() => setByDenomination((value) => !value)}
              className="text-sm text-brand-600 underline-offset-2 hover:underline"
            >
              {byDenomination ? "Type the total instead" : "Count notes and coins"}
            </button>
          </div>

          {byDenomination ? (
            // Two columns of fixed-width triples — label, box, line total. An
            // auto-flowing grid put one denomination's label beside another's
            // box, which is not a cosmetic problem when the thing being typed
            // is how many hundred-rand notes are in the drawer.
            <div className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
              {DENOMINATIONS.map((denomination) => {
                const quantity = counts[denomination.value] ?? "";
                const line =
                  Number(quantity) > 0
                    ? new Decimal(denomination.value).times(Math.floor(Number(quantity)))
                    : null;

                return (
                  <label
                    key={denomination.value}
                    className="grid grid-cols-[3rem_4.5rem_1fr] items-center gap-2"
                  >
                    <span
                      className={cn(
                        "text-sm font-medium",
                        denomination.kind === "note" ? "text-ink-900" : "text-ink-600",
                      )}
                    >
                      {denomination.label}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      value={quantity}
                      onChange={(event) =>
                        setCounts((current) => ({
                          ...current,
                          [denomination.value]: event.target.value,
                        }))
                      }
                      className="h-9 w-full rounded-control border border-line bg-surface px-2 text-sm tabular-nums text-ink-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                      aria-label={`How many ${denomination.label}`}
                    />
                    <span className="truncate text-right text-xs tabular-nums text-ink-500">
                      {line ? formatMoney(line.toFixed(2)) : ""}
                    </span>
                  </label>
                );
              })}
            </div>
          ) : (
            <TextField
              label="Counted cash (R)"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={typedTotal}
              onChange={(event) => setTypedTotal(event.target.value)}
              autoFocus
            />
          )}

          <div className="flex items-baseline justify-between rounded-card border border-line bg-canvas px-4 py-3">
            <span className="text-sm text-ink-600">Counted</span>
            <span className="text-2xl font-semibold tabular-nums text-ink-900">
              {countedTotal === null ? "—" : formatMoney(countedTotal.toFixed(2))}
            </span>
          </div>

          <TextField
            label="Note (optional)"
            value={note}
            maxLength={300}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Anything that explains the drawer"
          />
        </div>

        {/* ── What the shift took ─────────────────────────────────────────── */}
        <div className="space-y-1.5 rounded-card border border-line bg-canvas p-4 text-sm">
          <p className="mb-2 font-medium text-ink-900">This shift</p>
          <Row
            label={shift.opened_automatically ? "Opened with (not counted)" : "Opened with"}
            value={shift.opening_float}
          />
          {totals && (
            <>
              <Row label="Cash sales" value={totals.cash_sales} />
              <Row label="Card sales" value={totals.card_sales} muted />
              {Number(totals.other_sales) > 0 && (
                <Row label="Other tenders" value={totals.other_sales} muted />
              )}
              {Number(totals.pay_ins) > 0 && <Row label="Cash in" value={totals.pay_ins} />}
              {Number(totals.pay_outs) > 0 && <Row label="Cash out" value={totals.pay_outs} />}
              <p className="pt-1 text-xs text-ink-500">{plural(totals.sale_count, "sale")}</p>
            </>
          )}
          <p className="mt-3 border-t border-line pt-3 text-xs text-ink-500">
            Card and account sales never reach the drawer. They are shown so a shortage that is
            really a card sale rung up as cash can be spotted.
          </p>
        </div>
      </div>

      {close.error != null && (
        <p className="mt-3 text-sm text-danger-700">
          {close.error instanceof Error ? close.error.message : "That did not work."}
        </p>
      )}
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// After the count
// ═════════════════════════════════════════════════════════════════════════════
function Outcome({
  shift,
  queued,
  canApprove,
  approving,
  onApprove,
  onClose,
}: {
  shift: Shift;
  queued: boolean;
  canApprove: boolean;
  approving: boolean;
  onApprove: () => void;
  onClose: () => void;
}) {
  const variance = shift.variance === null ? null : new Decimal(shift.variance);
  const balanced = variance !== null && variance.isZero();
  const short = variance !== null && variance.isNegative();

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title="Shift closed"
      footer={
        <>
          {canApprove && variance !== null && !balanced && shift.status === "CLOSED" && (
            <Button loading={approving} onClick={onApprove}>
              Approve the difference
            </Button>
          )}
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {queued ? (
          <p className="rounded-card border border-warn-500/30 bg-warn-50 px-4 py-3 text-sm text-ink-600">
            The count is saved on this terminal and will upload when the signal returns. The
            difference is worked out by the server from the sales it has, so it appears once the
            queue has drained — a figure this device guessed would be worse than waiting.
          </p>
        ) : (
          <>
            <div className="space-y-1.5 text-sm">
              <Row label="Counted" value={shift.counted_cash ?? "0.00"} />
              {/* Present only for somebody permitted to see it. */}
              {shift.expected_cash !== null && (
                <Row label="Expected" value={shift.expected_cash ?? "0.00"} />
              )}
            </div>

            {variance !== null && (
              <div
                className={cn(
                  "flex items-center gap-3 rounded-card border px-4 py-3",
                  balanced
                    ? "border-brand-500/30 bg-brand-50"
                    : short
                      ? "border-danger-500/30 bg-danger-50"
                      : "border-warn-500/30 bg-warn-50",
                )}
              >
                {balanced ? (
                  <Check size={20} className="text-brand-600" aria-hidden />
                ) : (
                  <TriangleAlert
                    size={20}
                    className={short ? "text-danger-700" : "text-warn-700"}
                    aria-hidden
                  />
                )}
                <div>
                  <p className="font-medium text-ink-900">
                    {balanced
                      ? "The drawer balances"
                      : short
                        ? `Short by ${formatMoney(variance.abs().toFixed(2))}`
                        : `Over by ${formatMoney(variance.abs().toFixed(2))}`}
                  </p>
                  {/* The likeliest explanation, offered before somebody guesses
                      at a worse one. Nobody counted the drawer at the start, so
                      the expected figure never included whatever float was in
                      there — and the difference shows up here as a surplus. */}
                  {!balanced && shift.opened_automatically && !short && (
                    <p className="text-sm text-ink-600">
                      Nobody opened this shift — the first sale did, so the float in the drawer
                      was never counted. That is most likely what this surplus is. Opening the
                      shift and counting the float at the start makes tomorrow&rsquo;s figure mean
                      something.
                    </p>
                  )}
                  {!balanced && !(shift.opened_automatically && !short) && (
                    <p className="text-sm text-ink-600">
                      Recorded, not absorbed. A till out by a few rand is ordinary; what matters is
                      whether it happens every day.
                    </p>
                  )}
                </div>
              </div>
            )}

            {shift.status === "RECONCILED" && (
              <p className="text-sm text-ink-600">
                Approved by {shift.approved_by_name ?? "a manager"}.
              </p>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className={muted ? "text-ink-500" : "text-ink-600"}>{label}</span>
      <span className={cn("tabular-nums", muted ? "text-ink-500" : "font-medium text-ink-900")}>
        {formatMoney(value)}
      </span>
    </div>
  );
}
